import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, LinearProgress, Chip, Button, Collapse,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as UncheckedIcon,
  RocketLaunch as RocketIcon,
  VisibilityOff as DismissIcon,
} from '@mui/icons-material';
import api, { getTenantSettings } from '../api';

const STEPS = [
  { key: 'company', label: 'Company Info' },
  { key: 'employees', label: 'Employees' },
  { key: 'companies', label: 'Client Companies' },
  { key: 'payPeriods', label: 'Pay Periods' },
];

export default function SetupBanner() {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem('hourtrax_setup_dismissed') === 'true'; } catch { return false; }
  });

  useEffect(() => {
    if (dismissed) return;
    const check = async () => {
      try {
        const [tenantRes, empRes, compRes, ppRes] = await Promise.all([
          getTenantSettings(),
          api.get('/employees'),
          api.get('/companies'),
          api.get('/pay-periods'),
        ]);
        const t = tenantRes.data || {};
        setStatus({
          company: !!(t.display_name || t.legal_name) && !!t.ein,
          employees: (empRes.data || []).length > 0,
          companies: (compRes.data || []).length > 0,
          payPeriods: (ppRes.data || []).length > 0,
        });
      } catch { /* ignore */ }
    };
    check();
  }, [dismissed]);

  if (dismissed || !status) return null;

  const completed = STEPS.filter(s => status[s.key]).length;
  const total = STEPS.length;
  const allDone = completed === total;

  if (allDone) return null;

  const progress = Math.round((completed / total) * 100);

  const handleDismiss = () => {
    setDismissed(true);
    try { localStorage.setItem('hourtrax_setup_dismissed', 'true'); } catch { /* ignore */ }
  };

  return (
    <Collapse in={!dismissed}>
      <Paper sx={{
        p: 2.5, mb: 3,
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2563EB 100%)',
        color: 'white',
        position: 'relative',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <RocketIcon sx={{ mr: 1, fontSize: 28 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Initial Setup — {completed} of {total} steps complete
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              Complete these steps to start tracking time, invoicing clients, and running payroll.
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={<DismissIcon />}
            onClick={handleDismiss}
            sx={{
              color: 'rgba(255,255,255,0.7)',
              textTransform: 'none',
              '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.1)' },
            }}
          >
            Dismiss
          </Button>
        </Box>

        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            mb: 1.5, height: 8, borderRadius: 4,
            backgroundColor: 'rgba(255,255,255,0.2)',
            '& .MuiLinearProgress-bar': { backgroundColor: '#34D399', borderRadius: 4 },
          }}
        />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {STEPS.map(step => (
            <Chip
              key={step.key}
              icon={status[step.key]
                ? <CheckIcon sx={{ color: '#34D399 !important', fontSize: 18 }} />
                : <UncheckedIcon sx={{ color: 'rgba(255,255,255,0.5) !important', fontSize: 18 }} />
              }
              label={step.label}
              size="small"
              sx={{
                color: 'white',
                backgroundColor: status[step.key] ? 'rgba(52,211,153,0.2)' : 'rgba(255,255,255,0.1)',
                borderColor: status[step.key] ? 'rgba(52,211,153,0.4)' : 'rgba(255,255,255,0.2)',
                border: '1px solid',
                fontWeight: status[step.key] ? 600 : 400,
              }}
            />
          ))}
        </Box>

        <Typography variant="caption" sx={{ display: 'block', mt: 1.5, opacity: 0.7 }}>
          See the Setup Guide under the Help tab for detailed setup instructions.
        </Typography>
      </Paper>
    </Collapse>
  );
}
